import { Permission } from '@condo/domain';
import {
    BookOpen,
    Calculator,
    ChartNoAxesColumn,
    ChevronDown,
    ClipboardCheck,
    Filter,
    Hammer,
    ListChecks,
    Save,
    ServerCog,
    Settings,
    ShieldAlert,
    SlidersHorizontal,
    Sparkles,
    SquareKanban,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useCurrentUserDetails, useHasPermissions } from '@/entities/user';
import { FeatureFlag } from '@/shared/feature-flags/feature-flags';
import { useFeatureFlag } from '@/shared/feature-flags/hooks/useFeatureFlag';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/components/ui/collapsible';
import {
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuItem,
    SidebarProvider,
} from '@/shared/components/ui/sidebar';
import { useIsEmbedded } from '@/shared/hooks/useIsEmbedded';
import { cn } from '@/shared/utils';
import { useConfig } from '@/shared/utils/config/config-context';
import type { LucideIcon } from 'lucide-react';

interface ConfigNavItem {
    label: string;
    path: string;
    icon: LucideIcon;
    description: string;
    /** Replaces the shared Configuration header line when this item is active. */
    summary?: string;
}

interface ConfigNavGroup {
    label: string;
    icon: LucideIcon;
    items: ConfigNavItem[];
}

const CONFIG_HEADER_SUMMARY = 'manage RAM configuration for investment-case defaults and partner-specific input profiles.';

const CONFIG_NAV_GROUPS: ConfigNavGroup[] = [
    {
        label: 'Investment Case',
        icon: ChartNoAxesColumn,
        items: [
            {
                label: 'Global Defaults',
                path: '/configuration/investment-case-defaults',
                icon: Save,
                description: 'Global IC assumptions',
            },
            {
                label: 'Asset Partner Profiles',
                path: '/configuration/asset-partner-ic-profiles',
                icon: SlidersHorizontal,
                description: 'Partner profile configs',
            },
        ],
    },
    {
        label: 'Due Diligence',
        icon: ClipboardCheck,
        items: [
            {
                label: 'AI Suggestions',
                path: '/configuration/dd-answer-suggestions',
                icon: Sparkles,
                description: 'Answers the AI extracted from DD documents',
                summary: 'Answers the AI extracted from due diligence documents, and what reviewers made of them.',
            },
        ],
    },
    {
        label: 'Renovation',
        icon: Hammer,
        items: [
            {
                label: 'Renovation strategy',
                path: '/configuration/renovation-strategy-scenarios',
                icon: SquareKanban,
                description: 'Catalogue renovation strategy templates and their input parameters',
            },
        ],
    },
    {
        label: 'Sourcing',
        icon: Filter,
        items: [
            {
                label: 'Sourcing criteria',
                path: '/configuration/sourcing-criteria',
                icon: ListChecks,
                description: 'MFH sourcing criteria for every region',
            },
        ],
    },
];

const CAPEX_CONFIG_NAV_GROUP: ConfigNavGroup = {
    label: 'CAPEX & EE',
    icon: Calculator,
    items: [
        {
            label: 'Calculator Config',
            path: '/configuration/capex-ee-calculator',
            icon: Calculator,
            description: 'Global catalogue and calculation parameters',
            summary: 'Global measure catalogue and calculation parameters for the CAPEX & Energy Efficiency calculator.',
        },
    ],
};

const SYSTEM_CONFIG_NAV_GROUP: ConfigNavGroup = {
    label: 'System',
    icon: ServerCog,
    items: [
        {
            label: 'Cache Management',
            path: '/configuration/cache-management',
            icon: Hammer,
            description: 'Clear use case, repository, HERE, and RPC caches',
            summary: 'Manage and reset application, repository, HERE geocoding, and RPC caches across services.',
        },
        {
            label: 'Secret Admin Area',
            path: '/configuration/secret-admin',
            icon: ShieldAlert,
            description: 'Run data migrations and execute arbitrary use cases',
            summary: 'Administrative maintenance tools for running data migrations and executing backend use cases.',
        },
    ],
};

const LEARNING_CONFIG_NAV_GROUP: ConfigNavGroup = {
    label: 'Learning',
    icon: BookOpen,
    items: [
        {
            label: 'Learning questions',
            path: '/configuration/spotlights',
            icon: BookOpen,
            description: 'Maintain questions and images',
            summary: 'Create and preview contextual learning questions.',
        },
        {
            label: 'Learning coverage',
            path: '/configuration/spotlights/report',
            icon: ChartNoAxesColumn,
            description: 'Question reach and responses',
            summary: 'Review question reach, answers and repeated dismissals.',
        },
    ],
};

const ALL_CONFIG_NAV_ITEMS = [...CONFIG_NAV_GROUPS, CAPEX_CONFIG_NAV_GROUP, LEARNING_CONFIG_NAV_GROUP, SYSTEM_CONFIG_NAV_GROUP].flatMap(group => group.items);

function configNavLinkClassName(isActive: boolean): string {
    return cn(
        'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm outline-none transition-colors',
        'focus-visible:ring-2 focus-visible:ring-ring',
        isActive ? 'bg-gray-200 font-medium text-black' : 'text-black hover:bg-gray-100 hover:text-black',
    );
}

function getActiveConfigItem(pathname: string): ConfigNavItem {
    return (
        ALL_CONFIG_NAV_ITEMS.find(item => pathname === item.path) ??
        ALL_CONFIG_NAV_ITEMS.find(item => pathname.startsWith(item.path)) ??
        ALL_CONFIG_NAV_ITEMS[0]
    );
}

function useVisibleConfigNavGroups(): ConfigNavGroup[] {
    const { t } = useTranslation(['main', 'spotlight-admin']);
    const { hasPermission } = useHasPermissions();
    const { isAdmin } = useCurrentUserDetails();
    const { config } = useConfig();
    const configurationGroups = CONFIG_NAV_GROUPS.map(group => ({
        ...group,
        items: group.items.filter(() => hasPermission(Permission.MFHInvestmentCaseAdminInputEdit)),
    })).filter(group => group.items.length > 0);
    const enableCapexCalculator = useFeatureFlag(FeatureFlag.F_CAPEX_CALCULATOR);
    const capexGroup: ConfigNavGroup = {
        ...CAPEX_CONFIG_NAV_GROUP,
        items: CAPEX_CONFIG_NAV_GROUP.items.map(item => ({
            ...item,
            label: t('nav.items.capexEeCalculatorConfig'),
        })),
    };
    const isProduction = config?.ENVIRONMENT_NAME === 'production';
    const systemGroup: ConfigNavGroup = {
        label: t('adminSystem.navGroup'),
        icon: ServerCog,
        items: [
            {
                label: t('nav.items.cacheManagement'),
                path: '/configuration/cache-management',
                icon: Hammer,
                description: t('adminSystem.cacheManagement.navDescription'),
                summary: t('adminSystem.cacheManagement.summary'),
            },
            ...(!isProduction
                ? [
                      {
                          label: t('nav.items.secretAdminArea'),
                          path: '/configuration/secret-admin',
                          icon: ShieldAlert,
                          description: t('adminSystem.secretAdmin.navDescription'),
                          summary: t('adminSystem.secretAdmin.summary'),
                      },
                  ]
                : []),
        ],
    };

    const learningItems = LEARNING_CONFIG_NAV_GROUP.items.filter(item =>
        hasPermission(item.path.endsWith('/spotlights') ? Permission.SpotlightManage : Permission.SpotlightReportView),
    );
    return [
        ...configurationGroups,
        ...(enableCapexCalculator ? [capexGroup] : []),
        ...(learningItems.length
            ? [
                  {
                      ...LEARNING_CONFIG_NAV_GROUP,
                      label: t('spotlight-admin:learning'),
                      items: learningItems.map(item => ({
                          ...item,
                          label: t(item.path.endsWith('/spotlights') ? 'spotlight-admin:heading' : 'spotlight-admin:reportHeading'),
                      })),
                  },
              ]
            : []),
        ...(isAdmin ? [systemGroup] : []),
    ];
}

function ConfigHeader() {
    const location = useLocation();
    const activeItem = getActiveConfigItem(location.pathname);

    return (
        <Card>
            <CardContent className="p-6">
                <header className="space-y-4">
                    <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex min-w-0 flex-1 gap-4">
                            <div
                                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-[#012A3C]/15 bg-[#012A3C]/10"
                                aria-hidden
                            >
                                <Settings className="h-6 w-6 text-[#012A3C] dark:text-[#46e166]" />
                            </div>
                            <div className="min-w-0 flex-1 space-y-1.5">
                                <h1 className="text-2xl font-semibold leading-tight tracking-tight text-foreground">Configuration</h1>
                                <p className="text-muted-foreground text-sm">{activeItem.summary ?? `${activeItem.label}: ${CONFIG_HEADER_SUMMARY}`}</p>
                            </div>
                        </div>
                    </div>
                </header>
            </CardContent>
        </Card>
    );
}

function ConfigSidebarNav() {
    const location = useLocation();
    const groups = useVisibleConfigNavGroups();

    return (
        <Card className="flex w-[220px] shrink-0 flex-col self-stretch bg-white text-black">
            <CardContent className="flex flex-1 flex-col p-0">
                <div className="w-full flex-1 px-2 py-2">
                    <Sidebar className="h-auto min-h-0 w-full shrink-0 border-0 bg-transparent">
                        <SidebarContent className="min-h-0 flex-none !overflow-visible">
                            {groups.map(group => {
                                const GroupIcon = group.icon;

                                return (
                                    <Collapsible key={group.label} defaultOpen className="group/collapsible">
                                        <SidebarGroup>
                                            <SidebarGroupLabel asChild>
                                                <CollapsibleTrigger className="flex w-full items-center gap-2 py-1.5 text-sm font-bold !text-gray-900 hover:bg-gray-100">
                                                    <ChevronDown className="h-4 w-4 shrink-0 transition-transform" />
                                                    <GroupIcon className="h-4 w-4 shrink-0" />
                                                    <span>{group.label}</span>
                                                </CollapsibleTrigger>
                                            </SidebarGroupLabel>
                                            <CollapsibleContent>
                                                <SidebarGroupContent className="pl-5">
                                                    <SidebarMenu>
                                                        {group.items.map(item => {
                                                            const Icon = item.icon;
                                                            const isActive = getActiveConfigItem(location.pathname).path === item.path;

                                                            return (
                                                                <SidebarMenuItem key={item.path}>
                                                                    <Link
                                                                        to={item.path}
                                                                        data-slot="sidebar-menu-button"
                                                                        data-active={isActive ? true : undefined}
                                                                        className={configNavLinkClassName(isActive)}
                                                                        aria-current={isActive ? 'page' : undefined}
                                                                    >
                                                                        <Icon
                                                                            className={cn('h-4 w-4 shrink-0', isActive ? 'text-blue-600' : 'text-slate-600')}
                                                                            aria-hidden
                                                                        />
                                                                        <span className="min-w-0 truncate">{item.label}</span>
                                                                    </Link>
                                                                </SidebarMenuItem>
                                                            );
                                                        })}
                                                    </SidebarMenu>
                                                </SidebarGroupContent>
                                            </CollapsibleContent>
                                        </SidebarGroup>
                                    </Collapsible>
                                );
                            })}
                        </SidebarContent>
                    </Sidebar>
                </div>
            </CardContent>
        </Card>
    );
}

export function ConfigLayout() {
    const isEmbedded = useIsEmbedded();

    return (
        <div className="flex w-full flex-col gap-6 p-6">
            <ConfigHeader />

            <SidebarProvider className="flex flex-1 flex-col">
                <div className="flex min-w-0 flex-1 items-stretch gap-6">
                    {!isEmbedded && <ConfigSidebarNav />}
                    <main className="min-w-0 flex-1">
                        <Outlet />
                    </main>
                </div>
            </SidebarProvider>
        </div>
    );
}

export function ConfigIndexRedirect() {
    const groups = useVisibleConfigNavGroups();
    return <Navigate to={groups[0]?.items[0]?.path ?? '/'} replace />;
}
